/**
 * The domain vocabulary, defined once for the server and the SPA.
 *
 * Schemas are the source of truth and types are `z.infer`, so a single
 * definition serves Fastify's body validation, the client's parsing and the
 * socket payloads. Duplicating any of these as a hand-written interface on the
 * other side is how the two halves drift: the API starts sending a field the
 * client silently ignores, and nobody notices until a board row renders blank.
 *
 * This directory imports NOTHING but zod. A single import of a server module
 * here would drag the database driver into the browser bundle, and a circular
 * import between the two halves becomes possible; keeping the dependency list
 * at one package makes both impossible by construction (enforced in
 * eslint.config.js).
 */

import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Timestamps cross the wire as ISO-8601 with an offset, which is what
 * `JSON.stringify` does to a `Date`. Kept strict so a route that starts sending
 * epoch milliseconds fails a test rather than reaching the client.
 */
export const isoDateTime = z.string().datetime({ offset: true });

/**
 * Keyset cursor: the sort timestamp in epoch milliseconds, then the row id.
 * Opaque to the client, but readable in a log — which offset pagination bugs
 * never are. Split on the FIRST underscore: ids carry their own.
 */
export const cursorSchema = z.string().regex(/^\d+_.+$/, 'malformed cursor');

export const handlingStateSchema = z.enum(['ai', 'parked', 'human', 'resolved']);
export type HandlingState = z.infer<typeof handlingStateSchema>;

export const parkReasonSchema = z.enum([
  'asked_for_human',
  'frustrated',
  'ai_stuck',
  'low_confidence',
  'money_question',
  'missing_data',
  'complaint',
  'ai_error',
  'out_of_hours',
  'manual',
]);
export type ParkReason = z.infer<typeof parkReasonSchema>;

export const temperatureSchema = z.enum(['hot', 'warm', 'cold']);
export type Temperature = z.infer<typeof temperatureSchema>;

export const messageDirectionSchema = z.enum(['in', 'out']);
export type MessageDirection = z.infer<typeof messageDirectionSchema>;

export const senderTypeSchema = z.enum(['contact', 'agent', 'ai', 'system']);
export type SenderType = z.infer<typeof senderTypeSchema>;

export const messageTypeSchema = z.enum([
  'text',
  'image',
  'document',
  'audio',
  'video',
  'interactive',
  'template',
  'note',
  'system_event',
  'unsupported',
]);
export type MessageType = z.infer<typeof messageTypeSchema>;

export const deliveryStatusSchema = z.enum(['queued', 'sent', 'delivered', 'read', 'failed']);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

export const userRoleSchema = z.enum(['admin', 'agent']);
export type UserRole = z.infer<typeof userRoleSchema>;

export const languageSchema = z.enum(['en', 'sw']);
export type Language = z.infer<typeof languageSchema>;

/* -------------------------------------------------------------------------- */
/* Conversation state — owned here, folded by the server's pure reducer        */
/* -------------------------------------------------------------------------- */

/**
 * Structured facts, not a narrative summary. A returning candidate is
 * reconstructed from this, so it must survive weeks without decaying — which
 * rules out an LLM-written prose summary as the primary store.
 *
 * Stored as jsonb and read back as a Partial: the column starts as `{}` and
 * rows written before a field existed will not have it.
 */
export const candidateFactsSchema = z.object({
  name: z.string().optional(),
  gender: z.enum(['M', 'F']).nullable().optional(),
  language: languageSchema,
  /** What they said they do, verbatim. */
  statedProfession: z.string().optional(),
  /** Demand ids we matched and showed them. */
  matchedDemandIds: z.array(z.string()),
  /** The role they chose to pursue. */
  chosenDemandId: z.string().optional(),
  /** Answer to "do you already hold a licence in the destination country?" */
  holdsLocalLicence: z.boolean().optional(),
  /** Slots we have received something for. */
  documentsReceived: z.array(z.string()),
  /** Slots still outstanding for the chosen role. */
  documentsOutstanding: z.array(z.string()),
  /** Questions the AI could not answer, kept so a human can pick them up. */
  openQuestions: z.array(z.string()),
  /**
   * What the human who handed the conversation back told the AI to do. Lives
   * with the facts rather than in a message row because the model reads this
   * object instead of the transcript.
   */
  staffNote: z.string().optional(),
  /** Set once the lead reaches the ERP. */
  submittedToErp: z.boolean().optional(),
  erpStatus: z.string().optional(),
  /**
   * The office appointment they hold, ISO-8601. Read by staff and by
   * `resumeState`; deliberately NOT rendered into the model's context, because
   * the guardrail refuses any figure absent from a demand record and a clock
   * time is a figure — the appointment is only ever stated by deterministic
   * copy, which never passes through the model.
   */
  appointmentAt: z.string().optional(),
  /** They have already been chased once about choosing a time. */
  appointmentNudged: z.boolean().optional(),
});
export type CandidateFacts = z.infer<typeof candidateFactsSchema>;

export const flowStateSchema = z.discriminatedUnion('kind', [
  /** Never messaged us before. */
  z.object({ kind: z.literal('new') }),
  /** Asked which language they prefer. */
  z.object({ kind: z.literal('awaiting_language') }),
  /** Asked what work they are looking for. */
  z.object({ kind: z.literal('awaiting_profession') }),
  /** Showed matching roles, waiting for them to pick one. */
  z.object({ kind: z.literal('awaiting_role_choice'), offered: z.array(z.string()) }),
  /** Their chosen role needs an in-country licence; asked whether they hold one. */
  z.object({ kind: z.literal('awaiting_licence_answer'), demandId: z.string() }),
  /** Collecting documents for the chosen role. */
  z.object({ kind: z.literal('collecting_documents'), demandId: z.string() }),
  /** Everything gathered; ready to submit to the ERP. */
  z.object({ kind: z.literal('ready_to_submit'), demandId: z.string() }),
  /** Submitted; waiting on ERP verification. */
  z.object({ kind: z.literal('submitted'), demandId: z.string() }),
  /**
   * Shown up to three real appointment times, waiting for a tap. The offer is
   * recorded here rather than as rows in `appointments`, because an offer must
   * not hold a place — 50 a day against ~860 enquiries would be gone after
   * seventeen conversations if it did.
   */
  z.object({
    kind: z.literal('awaiting_appointment_choice'),
    offered: z.array(z.object({ startsAt: isoDateTime, desk: z.number().int() })),
  }),
  /**
   * They tapped a time and the claim is in flight. Nothing has been promised
   * yet: the confirmation is only sent once the database says this candidate
   * owns the place.
   */
  z.object({
    kind: z.literal('booking_appointment'),
    startsAt: isoDateTime,
    desk: z.number().int(),
  }),
  /** They have a time at the office. */
  z.object({ kind: z.literal('appointment_booked'), startsAt: isoDateTime }),
  /** No role matches their trade. Kept warm rather than closed. */
  z.object({ kind: z.literal('no_match'), statedProfession: z.string() }),
  /** A human owns this conversation; the reducer stands down entirely. */
  z.object({ kind: z.literal('human') }),
  /** Finished — rejected, withdrawn, or placed. */
  z.object({ kind: z.literal('closed'), reason: z.string() }),
]);
export type FlowState = z.infer<typeof flowStateSchema>;

/** A reply the sender renders into a WhatsApp payload. */
export const outboundIntentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), body: z.string() }),
  z.object({
    kind: z.literal('buttons'),
    body: z.string(),
    buttons: z.array(z.object({ id: z.string(), title: z.string() })),
  }),
  z.object({
    kind: z.literal('list'),
    body: z.string(),
    buttonLabel: z.string(),
    rows: z.array(
      z.object({ id: z.string(), title: z.string(), description: z.string().optional() }),
    ),
  }),
]);
export type OutboundIntent = z.infer<typeof outboundIntentSchema>;

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                 */
/* -------------------------------------------------------------------------- */

/** Just enough of a staff user to render an avatar and a name. */
export const userSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  role: userRoleSchema,
});
export type UserSummary = z.infer<typeof userSummarySchema>;

export const messageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  direction: messageDirectionSchema,
  senderType: senderTypeSchema,
  senderId: z.string().nullable(),
  type: messageTypeSchema,
  body: z.string().nullable(),
  /** Internal note. Never sent to the candidate, only ever shown to staff. */
  isPrivate: z.boolean(),
  deliveryStatus: deliveryStatusSchema.nullable(),
  failureReason: z.string().nullable(),
  documentId: z.string().nullable(),
  aiModel: z.string().nullable(),
  createdAt: isoDateTime,
});
export type Message = z.infer<typeof messageSchema>;

export const contactSummarySchema = z.object({
  id: z.string(),
  phone: z.string(),
  /** WhatsApp profile name — untrusted, candidate-controlled. */
  pushName: z.string().nullable(),
  /** Name as read off the passport, or entered by staff. Trusted. */
  name: z.string().nullable(),
  language: z.string(),
  professions: z.array(z.string()),
});
export type ContactSummary = z.infer<typeof contactSummarySchema>;

/**
 * One row of the conversation list. `rev` is `updatedAt` in epoch milliseconds:
 * the client drops any socket patch whose `rev` is not newer than what it holds,
 * which is what makes out-of-order broadcasts harmless. A dedicated revision
 * column would have to be bumped by every writer including the turn handler and
 * the sender worker; `updated_at` already is.
 */
export const conversationSummarySchema = z.object({
  id: z.string(),
  rev: z.number(),
  contact: contactSummarySchema,
  handlingState: handlingStateSchema,
  parkReason: parkReasonSchema.nullable(),
  parkSummary: z.string().nullable(),
  parkedAt: isoDateTime.nullable(),
  assignee: userSummarySchema.nullable(),
  temperature: temperatureSchema,
  temperatureScore: z.number(),
  attentionScore: z.number(),
  attentionFlags: z.array(z.string()),
  /** The trade we are working with: what they said, else what staff recorded. */
  trade: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: isoDateTime.nullable(),
  lastInboundAt: isoDateTime.nullable(),
  messageCount: z.number(),
  unreadCount: z.number(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

/** A conversation opened in the thread view: the summary plus its state. */
export const conversationDetailSchema = conversationSummarySchema.extend({
  waConversationId: z.string(),
  flowState: flowStateSchema,
  facts: candidateFactsSchema.partial(),
  matchedDemandIds: z.array(z.string()),
  adelStatus: z.string().nullable(),
  adelSubmittedAt: isoDateTime.nullable(),
});
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

/**
 * A Live Board row. Deliberately narrower than the summary: this list re-renders
 * several times a second, so it carries only what a row actually draws.
 */
export const boardRowSchema = z.object({
  conversationId: z.string(),
  rev: z.number(),
  candidateName: z.string(),
  phone: z.string(),
  trade: z.string().nullable(),
  handlingState: handlingStateSchema,
  assignee: userSummarySchema.nullable(),
  temperature: temperatureSchema,
  attentionScore: z.number(),
  attentionFlags: z.array(z.string()),
  parkReason: parkReasonSchema.nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: isoDateTime.nullable(),
  /**
   * Milliseconds since the last message, measured on the server. The client
   * ticks its own clock from here rather than trusting the browser's idea of
   * "now", which on a laptop resumed from sleep can be minutes out.
   */
  idleMs: z.number(),
});
export type BoardRow = z.infer<typeof boardRowSchema>;

/** A row of the parked queue. Sorted by how long someone has been waiting. */
export const parkedRowSchema = boardRowSchema.extend({
  parkedAt: isoDateTime.nullable(),
  parkSummary: z.string().nullable(),
  /** Milliseconds since the AI parked it. The number staff are judged on. */
  waitingMs: z.number(),
});
export type ParkedRow = z.infer<typeof parkedRowSchema>;

/** A published ADEL role, as the SPA needs to show it. */
export const demandSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  country: z.string().nullable(),
  gender: z.string().nullable(),
  status: z.string(),
  /** False when salary/charge data is too incomplete to quote. Never estimate. */
  quotable: z.boolean(),
  requiresLocalLicence: z.boolean(),
  keywords: z.array(z.string()),
});
export type DemandSummary = z.infer<typeof demandSummarySchema>;

/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */

export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/**
 * Settings values are jsonb, so anything JSON-shaped is legal — but `undefined`
 * and functions are not, and a PUT body missing `value` entirely must fail
 * rather than write SQL NULL into a NOT NULL column.
 */
export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

/** Setting keys are used in URLs and stored as a primary key. */
export const settingKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_.]*$/, 'setting keys are lower_snake_case');

export const settingSchema = z.object({
  key: settingKeySchema,
  value: jsonValueSchema,
  updatedAt: isoDateTime,
  updatedBy: z.string().nullable(),
});
export type Setting = z.infer<typeof settingSchema>;
